import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Badge, badgeVariants } from '@/shared/ui/badge'
import { Input } from '@/shared/ui/input'
import { t } from '@/shared/lib/i18n'
import { cn } from '@/shared/lib/utils'

// Desktop filter triggers leave ~50–100px for tags; a second tag only stays readable
// when the tag area is at least 10rem (mobile sheet, wide report triggers).
const MAX_TRIGGER_TAGS = 2
const WIDE_TRIGGER_ONLY_CLASS = 'hidden @min-[10rem]:inline-flex'
const NARROW_TRIGGER_ONLY_CLASS = '@min-[10rem]:hidden'
const SELECTED_TAG_CLASS = 'border-border text-xs'
// Clear sits left of the chevron: button px-4 (1rem) + chevron (1rem) + gap-1 (0.25rem).
const CLEAR_CONTROL_RIGHT_CLASS = 'right-[2.25rem]'
// Lets a surrounding dialog leave Escape to the open dropdown instead of closing itself.
export const OPEN_MULTI_SELECT_SELECTOR = '[data-multi-select-open]'

const sameSelection = <TValue extends string | number>(
  left: TValue[],
  right: TValue[],
): boolean => {
  if (left.length !== right.length) return false
  const rightValues = new Set(right)
  return left.every((value) => rightValues.has(value))
}

export interface MultiSelectOption<TValue extends string | number = number> {
  value: TValue
  label: string
  disabled?: boolean
}

interface Props<TValue extends string | number = number> {
  options: MultiSelectOption<TValue>[]
  selected: TValue[]
  onChange: (selected: TValue[]) => void
  onClose?: (selected: TValue[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  showBadges?: boolean
  /** Controlled open state. Provide together with `onToggle` to let a parent
   * coordinate which dropdown is open (e.g. one-at-a-time filters). */
  expanded?: boolean
  onToggle?: () => void
}

export const MultiSelectCombobox = <TValue extends string | number = number>({
  options,
  selected,
  onChange,
  onClose,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage,
  className,
  showBadges = true,
  expanded,
  onToggle,
}: Props<TValue>) => {
  const isControlled = expanded !== undefined && onToggle !== undefined
  const [internalOpen, setInternalOpen] = useState(false)
  const open = isControlled ? expanded : internalOpen
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const wasOpenRef = useRef(open)

  // Always holds the latest selected value so async event handlers (mousedown) never
  // capture a stale closure. lastCommittedRef skips a second onClose when dismiss
  // happens after a tick already applied that same selection.
  const latestSelectedRef = useRef(selected)
  const lastCommittedRef = useRef(selected)
  useEffect(() => {
    latestSelectedRef.current = selected
    lastCommittedRef.current = selected
  }, [selected])

  const commitSelection = useCallback(
    (next: TValue[]) => {
      latestSelectedRef.current = next
      lastCommittedRef.current = next
      onClose?.(next)
    },
    [onClose],
  )

  const dismissIfChanged = useCallback(() => {
    const current = latestSelectedRef.current
    if (sameSelection(lastCommittedRef.current, current)) return
    commitSelection(current)
  }, [commitSelection])

  useEffect(() => {
    if (!isControlled) return
    if (wasOpenRef.current && !open) {
      dismissIfChanged()
    }
    wasOpenRef.current = open
  }, [isControlled, open, dismissIfChanged])

  useEffect(() => {
    if (isControlled || !open) return
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        dismissIfChanged()
        setInternalOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isControlled, open, dismissIfChanged])

  const handleTriggerClick = () => {
    setSearch('')
    if (isControlled) {
      onToggle()
      return
    }
    if (open) dismissIfChanged()
    setInternalOpen((prev) => !prev)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !open) return
    event.stopPropagation()
    setSearch('')
    if (isControlled) {
      onToggle()
    } else {
      dismissIfChanged()
      setInternalOpen(false)
    }
    triggerRef.current?.focus()
  }

  const toggleOption = (value: TValue) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value]
    onChange(next)
    commitSelection(next)
  }

  const removeOption = (value: TValue) => {
    const next = selected.filter((v) => v !== value)
    onChange(next)
    commitSelection(next)
  }

  const clearSelection = () => {
    onChange([])
    commitSelection([])
  }

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()),
  )

  // Sort: selected+enabled → enabled → disabled.
  // Backend returns options sorted by name, so stable sort preserves alphabetical order per group.
  const sortedOptions = [...filteredOptions].sort((a, b) => {
    if (a.disabled && !b.disabled) return 1
    if (!a.disabled && b.disabled) return -1
    const aSelected = selected.includes(a.value)
    const bSelected = selected.includes(b.value)
    if (aSelected && !bSelected) return -1
    if (!aSelected && bSelected) return 1
    return 0
  })

  const enabledOptions = sortedOptions.filter((o) => !o.disabled)
  const allFilteredSelected =
    enabledOptions.length > 0 && enabledOptions.every((o) => selected.includes(o.value))

  const selectedLabels = options.filter((o) => selected.includes(o.value))
  const showTriggerTags = !showBadges && selectedLabels.length > 0
  const triggerTags = selectedLabels.slice(0, MAX_TRIGGER_TAGS)
  const triggerTitle = showTriggerTags
    ? selectedLabels.map((option) => option.label).join(', ')
    : undefined

  const renderHiddenTagCount = (hiddenCount: number, visibilityClass: string) =>
    hiddenCount > 0 && (
      <span
        data-slot="multi-select-hidden-tag-count"
        className={cn(
          badgeVariants({ variant: 'secondary' }),
          SELECTED_TAG_CLASS,
          'shrink-0',
          visibilityClass,
        )}
      >
        {t('shared.multiSelect.hiddenTagCount', { count: hiddenCount })}
      </span>
    )

  const renderTriggerSummary = () => {
    if (selected.length === 0) {
      return <span className="truncate text-sm text-muted-foreground">{placeholder}</span>
    }
    if (!showTriggerTags) {
      return (
        <span className="truncate text-sm text-muted-foreground">
          {t('shared.multiSelect.selectedCount', { count: selected.length })}
        </span>
      )
    }
    return (
      <span
        data-slot="multi-select-trigger-tags"
        className="@container flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden"
      >
        {triggerTags.map((option, index) => (
          <span
            key={option.value}
            data-slot="multi-select-selected-tag"
            className={cn(
              badgeVariants({ variant: 'secondary' }),
              SELECTED_TAG_CLASS,
              'min-w-0',
              index > 0 && WIDE_TRIGGER_ONLY_CLASS,
            )}
          >
            <span className="truncate">{option.label}</span>
          </span>
        ))}
        {renderHiddenTagCount(selectedLabels.length - triggerTags.length, WIDE_TRIGGER_ONLY_CLASS)}
        {renderHiddenTagCount(selectedLabels.length - 1, NARROW_TRIGGER_ONLY_CLASS)}
      </span>
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn('relative', className)}
      onKeyDown={handleKeyDown}
      data-multi-select-open={open ? '' : undefined}
    >
      {/* Clear outside trigger: nested control + active:scale-95 made edge clicks flaky (REBUILD-497). */}
      <div className="relative">
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          type="button"
          title={triggerTitle}
          onClick={handleTriggerClick}
          className="w-full justify-between font-normal"
        >
          {renderTriggerSummary()}
          <span className="ml-2 flex shrink-0 items-center gap-1">
            {selected.length > 0 && <span className="size-6" aria-hidden="true" />}
            <ChevronDown
              className={cn('h-4 w-4 opacity-50 transition-transform', open && 'rotate-180')}
            />
          </span>
        </Button>
        {selected.length > 0 && (
          <button
            type="button"
            tabIndex={-1}
            className={cn(
              'absolute top-1/2 z-10 flex size-6 -translate-y-1/2 items-center justify-center rounded-sm hover:bg-muted',
              CLEAR_CONTROL_RIGHT_CLASS,
            )}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              clearSelection()
            }}
            aria-label={t('shared.multiSelect.clearFilter')}
          >
            <X className="h-3.5 w-3.5 opacity-60 hover:opacity-100" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[220px] rounded-md border bg-popover shadow-md">
          <div className="p-2">
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto px-1 pb-1">
            {sortedOptions.length === 0 ? (
              <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                {emptyMessage ?? t('shared.multiSelect.noResults')}
              </p>
            ) : (
              <>
                {enabledOptions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      const enabledValues = enabledOptions.map((o) => o.value)
                      const next = allFilteredSelected
                        ? selected.filter((v) => !enabledValues.includes(v))
                        : [...new Set([...selected, ...enabledValues])]
                      onChange(next)
                      commitSelection(next)
                    }}
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer border-b mb-1"
                  >
                    <div
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded-sm border shrink-0',
                        allFilteredSelected
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'opacity-50',
                      )}
                    >
                      {allFilteredSelected && <Check className="h-3 w-3" />}
                    </div>
                    <span className="min-w-0 truncate font-medium">
                      {t('shared.multiSelect.selectAll')}
                    </span>
                  </button>
                )}
                {sortedOptions.map((option) => {
                  const isSelected = selected.includes(option.value)
                  const isDisabled = option.disabled === true
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={isDisabled ? undefined : () => toggleOption(option.value)}
                      disabled={isDisabled}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
                        isDisabled
                          ? 'cursor-not-allowed opacity-40 text-muted-foreground'
                          : 'hover:bg-accent cursor-pointer',
                      )}
                    >
                      <div
                        className={cn(
                          'flex h-4 w-4 items-center justify-center rounded-sm border shrink-0',
                          isSelected
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'opacity-50',
                        )}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <span title={option.label} className="min-w-0 truncate">
                        {option.label}
                      </span>
                    </button>
                  )
                })}
              </>
            )}
          </div>
        </div>
      )}

      {showBadges && selectedLabels.length > 0 && (
        <div data-slot="multi-select-selected-tags" className="mt-1.5 flex flex-wrap gap-1.5">
          {selectedLabels.map((option) => (
            <Badge
              key={option.value}
              variant="secondary"
              data-slot="multi-select-selected-tag"
              className={cn('gap-1', SELECTED_TAG_CLASS)}
            >
              {option.label}
              <button
                type="button"
                aria-label={t('shared.multiSelect.removeTag', { label: option.label })}
                onClick={(event) => {
                  event.stopPropagation()
                  removeOption(option.value)
                }}
                className="ml-0.5 rounded-full outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
